/**
 * Shared position-taxonomy dedupe for `listPositions` consumers.
 *
 * The full position list (fetched with `first: POSITIONS_FULL_LIST_FIRST`
 * because the backend's 20-row default page silently cut off Goalkeeper —
 * SE-5095/SE-5481) exposes legacy generic rows (abbreviation: null) whose
 * names duplicate specific positions (e.g. a second "Goalkeeper"). Backend
 * data cleanup is out of scope per the SE-5481 product decision, so every
 * consumer (filter panels, compare submenu, add-player modals) must apply
 * the same client-side dedupe: collapse every row to one per name, keeping
 * the abbreviated row when one exists (dropping the legacy duplicate) and
 * otherwise keeping a single representative for uniquely- or
 * duplicately-named generic rows (e.g. "Winger").
 * @module utils/dedupePositionNodes
 */

/** Minimal shape of a position node needed for deduplication */
interface PositionNodeLike {
  readonly id: string;
  readonly name: string;
  readonly abbreviation?: string | null;
}

/**
 * Dedupes position edges by name (abbreviated row wins) and returns the
 * nodes sorted alphabetically so dropdowns and filter panels are scannable.
 * @param edges - Position connection edges from a `listPositions` query
 * @returns Deduped position nodes sorted by name
 * @example
 * dedupePositionNodes([
 *   { node: { id: "1", name: "Goalkeeper", abbreviation: null } },
 *   { node: { id: "2", name: "Goalkeeper", abbreviation: "GK" } },
 * ]);
 * // [{ id: "2", name: "Goalkeeper", abbreviation: "GK" }]
 */
const dedupePositionNodes = <T extends PositionNodeLike>(
  edges: readonly { readonly node: T }[] | null | undefined
): readonly T[] => {
  const allEdges = edges ?? [];
  // Prefer the abbreviated row; otherwise keep the first row seen for a
  // given name so rows sharing a name (with or without an abbreviation)
  // still collapse to a single entry.
  const byName = allEdges.reduce<Readonly<Record<string, T>>>((acc, edge) => {
    const existing = acc[edge.node.name];
    const preferThisEdge =
      !existing || (edge.node.abbreviation && !existing.abbreviation);
    return preferThisEdge ? { ...acc, [edge.node.name]: edge.node } : acc;
  }, {});
  return Object.values(byName).sort((a, b) => a.name.localeCompare(b.name));
};

export default dedupePositionNodes;
